import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppConfigService } from './app-config.service';
import { AppService } from './app.service';
import { SessionStateService } from './session-state.service';
import { SystemService } from './system.service';
import { UserConfigService } from './user-config.service';
import { UserApiService } from './user-api.service';
import { SessionApiService } from './session-api.service';
/**
 * Orquestación de sesión del usuario (logout / cleanup / navegación).
 *
 * Nota:
 * - `UserConfigService` es el dueño del estado del usuario (BehaviorSubjects, permisos, menús).
 * - Este servicio se encarga de acciones de sesión que impactan varias capas (storage, navegación, APIs).
 */
@Injectable({ providedIn: 'root' })
export class UserSessionService {
  /** True mientras se está cerrando sesión; evita que los catálogos llamen a PUT UserPreference al destruirse. */
  private _isLoggingOut = false;
  get isLoggingOut(): boolean {
    return this._isLoggingOut;
  }

  constructor(
    private readonly appService: AppService,
    private readonly appConfigService: AppConfigService,
    private readonly sessionState: SessionStateService,
    private readonly systemService: SystemService,
    private readonly userConfigService: UserConfigService,
    private readonly router: Router,
    private readonly userApiService: UserApiService,
    private readonly sessionApiService: SessionApiService,
  ) {
    // Escuchar solicitud de cierre de sesión (por ejemplo cuando el backend devuelve 401).
    // Importante: `AppService.logOut` es `Subject` para no dispararse al inicializar.
    this.appService.logOut.subscribe(() => {
      void this.logOut();
    });
  }

  /**
   * Logout completo (cierra la sesión del usuario principal).
   * Aunque el backend falle, se limpia sesión local y se redirige al login.
   *
   * Importante:
   * - NO debe limpiar MaskUserId. Eso solo debe ocurrir en `logOutMask()`.
   */
  async logOut(): Promise<void> {
    this._isLoggingOut = true;
    await Promise.allSettled([this.userApiService.logOut()]);
    this.logOutConfirmed();
  }

  /**
   * Logout por expiración (sin llamadas al backend).
   */
  logOutByExpiration(): void {
    this._isLoggingOut = true;
    this.logOutConfirmed();
  }

  /**
   * Salir de sesión enmascarada (sin cerrar sesión completa).
   */
  async logOutMask(): Promise<void> {
    await this.userApiService.logOutMasked();
    window.location.reload();
  }

  private logOutConfirmed(): void {
    // URL de login (auth).
    // Nota: esto funciona también en sesiones enmascaradas porque el backend ya entrega el usuario efectivo.
    const loginUrl = this.appConfigService.urlLogin;

    // Resetear estado de la aplicación
    this.appService.userUnauthorized = false;

    // Limpiar variables del localStorage relacionadas con la sesión + estado de usuario
    this.systemService.clearSessionData();

    // Notificar que la sesión ha terminado (para limpiar UI/timers)
    this.sessionState.sessionEnded.next(true);

    // Resetear configuración de API (servicesAPI vacío)
    this.appConfigService.setApisURL();

    // Limpiar estado de usuario (menús/permisos) para evitar UI stale si el login se muestra encima.
    this.userConfigService.clearUserState();
    this.sessionApiService.clearBootstrap();

    // Navegar al login y resetear flag para la próxima sesión
    void this.router.navigateByUrl(loginUrl).then(() => {
      this._isLoggingOut = false;
    });
  }
}

